import React, { useState, useEffect, useCallback } from 'react';
import { track } from '../services/analyticsTracker';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import billingApi, { Invoice, Plan } from '../services/billingApi';
import { formatPlanPrice } from '../services/planUpgradeBanner';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckCircle, AlertTriangle, Clock, Calendar, CreditCard, Loader2,
  DollarSign, X, Pause, ShieldCheck, Receipt,
  TrendingUp, ArrowUpRight, ArrowDownRight, Wallet,
} from 'lucide-react';

interface CondoCharge {
  id: string;
  charge_type: string;
  description: string;
  amount: number;
  currency: string;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue' | 'partial';
  paid_amount?: number;
  created_at: string;
}

interface CondoPayment {
  id: string;
  amount: number;
  currency: string;
  payment_method: string;
  status: string;
  reference_number?: string;
  created_at: string;
  verified_at?: string;
}

export default function BillingPortal() {
  const { user, activeCondoId, activeOwnedUnitId, ownedUnits } = useAuth();
  const [entitlements, setEntitlements] = useState<any>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [condoCharges, setCondoCharges] = useState<CondoCharge[]>([]);
  const [condoPayments, setCondoPayments] = useState<CondoPayment[]>([]);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [chargesError, setChargesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [unitPlans, setUnitPlans] = useState<Plan[]>([]);
  const [planCheckoutLoading, setPlanCheckoutLoading] = useState<string | null>(null);
  const [planCheckoutError, setPlanCheckoutError] = useState<string | null>(null);

  const fetchEntitlements = useCallback(async () => {
    try {
      const data = await billingApi.getEntitlements(activeCondoId);
      setEntitlements(data);
    } catch (err) {
      console.error('Error fetching entitlements:', err);
    }
  }, [activeCondoId]);

  const fetchInvoices = useCallback(async () => {
    try {
      const data = await billingApi.getInvoices();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setInvoices([]);
    }
  }, []);

  const fetchCondoCharges = useCallback(async () => {
    // Need a unitId — get from user context or localStorage
    const userData = user as any;
    let unitId = userData?.unitId || userData?.unit_id;
    if (!unitId) {
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        unitId = stored.unitId || stored.unit_id;
      } catch { /* ignore */ }
    }
    if (!unitId) {
      setCondoCharges([]);
      setCondoPayments([]);
      return;
    }
    setChargesLoading(true);
    setChargesError(null);
    try {
      const res = await api.get(`/financial/charges/unit/${unitId}`);
      const data = res.data?.data ?? res.data;
      const charges = Array.isArray(data?.charges) ? data.charges : Array.isArray(data) ? data : [];
      const payments = Array.isArray(data?.payments) ? data.payments : [];
      setCondoCharges(charges);
      setCondoPayments(payments);
    } catch (err: any) {
      console.error('Error fetching condo charges:', err);
      setChargesError(err?.message || 'Error al cargar estado de cuenta');
      setCondoCharges([]);
      setCondoPayments([]);
    } finally {
      setChargesLoading(false);
    }
  }, [user]);

  useEffect(() => {
    Promise.all([fetchEntitlements(), fetchInvoices(), fetchCondoCharges()])
      .finally(() => setLoading(false));
    // Unit/user-scoped plans (Entitlement Sprint 2): Home+ per unit, HomeUltra per user.
    billingApi.getPlans()
      .then((data) => setUnitPlans((data.plans || []).filter((plan) => plan.id === 'home_plus' || plan.id === 'home_ultra')))
      .catch(() => setUnitPlans([]));
  }, [fetchEntitlements, fetchInvoices, fetchCondoCharges]);

  const handleUnitPlanCheckout = async (plan: Plan) => {
    setPlanCheckoutError(null);
    if (plan.id === 'home_plus' && !activeOwnedUnitId) {
      setPlanCheckoutError('Selecciona una unidad activa (contexto "Mi hogar") para suscribir Home+ a esa unidad.');
      return;
    }
    setPlanCheckoutLoading(plan.id);
    try {
      const result = await billingApi.checkoutUnitPlan(
        plan.id as 'home_plus' | 'home_ultra',
        plan.id === 'home_plus' ? activeOwnedUnitId : undefined,
        'card'
      );
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      await fetchEntitlements();
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.code === 'UNIT_ALREADY_SUBSCRIBED' || data?.code === 'HOME_ULTRA_ALREADY_ACTIVE' || data?.code === 'ALL_UNITS_COVERED') {
        setPlanCheckoutError(data.error || 'Este plan ya está activo.');
      } else {
        setPlanCheckoutError(data?.error || 'No se pudo iniciar el pago. Intenta de nuevo.');
      }
    } finally {
      setPlanCheckoutLoading(null);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      await billingApi.cancel();
      await fetchEntitlements();
      setShowCancelModal(false);
    } catch (err) {
      console.error('Cancel error:', err);
      alert('Error al cancelar la suscripción. Intenta de nuevo.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleUpdatePayment = async () => {
    setActionLoading(true);
    try {
      const result = await billingApi.openPortal();
      track('payments_portal_opened');
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('Portal error:', err);
      alert('Error al abrir el portal de pagos.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--v-teal-700)]" />
      </div>
    );
  }

  const tier = entitlements?.tier || 'free';
  const trialDaysLeft = entitlements?.trial_days_left;
  const isTrialing = entitlements?.trial_active === true;
  const isFree = tier === 'free';
  const status = isTrialing ? 'trialing' : 'active';
  const planDisplayName = tier === 'home_plus' ? 'Home+' : tier.startsWith('condo_') ? 'Condominio' : tier.charAt(0).toUpperCase() + tier.slice(1);
  const isPastDue = false;


  // Compute account statement values from condo charges
  const upcomingCharges = condoCharges.filter(c => c.status === 'pending' || c.status === 'overdue');
  const paidCharges = condoCharges.filter(c => c.status === 'paid');
  const overdueCharges = condoCharges.filter(c => c.status === 'overdue');
  const totalBalance = upcomingCharges.reduce((sum, c) => {
    const remaining = c.status === 'partial' ? (c.amount - (c.paid_amount || 0)) : c.amount;
    return sum + remaining;
  }, 0);
  const totalPaid = condoPayments
    .filter(p => p.status === 'verified' || p.status === 'paid')
    .reduce((sum, p) => sum + p.amount, 0);
  const isDelinquent = overdueCharges.length > 0;
  const chargesCurrency = condoCharges[0]?.currency || 'MXN';

  const fmtMoney = (val: number, currency: string = chargesCurrency) => {
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(val);
    } catch {
      return `$${val.toLocaleString()}`;
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto h-full overflow-auto text-[var(--v-text-primary)]">
      <PageHeader
        title="Mi Plan y Estado de Cuenta"
        subtitle="Administra tu suscripción y pagos del condominio"
      />

      {/* Current Plan Status */}
      <Card className="mb-6 p-6 border-l-4 border-l-[var(--v-teal-700)]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-bold">Plan {planDisplayName}</h2>
              <StatusBadge status={status} isTrialing={!!isTrialing} isFree={isFree} />
            </div>

            {isTrialing && trialDaysLeft !== undefined && trialDaysLeft > 0 && (
              <div className="mt-3 p-3 bg-[var(--v-teal-50)] rounded-[var(--v-radius-md)]">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-[var(--v-teal-700)]" />
                  <span className="text-[var(--v-teal-700)] font-medium">
                    Te quedan {trialDaysLeft} {trialDaysLeft === 1 ? 'día' : 'días'} de prueba gratuita
                  </span>
                </div>
                <div className="mt-2 w-full bg-[var(--v-surface-2)] rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-[var(--v-teal-700)] h-full rounded-full transition-all"
                    style={{ width: `${Math.max(10, Math.min(100, (trialDaysLeft / 30) * 100))}%` }}
                  />
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={() => window.location.href = '/billing/checkout?plan=' + tier}
                >
                  Activar plan ahora
                </Button>
              </div>
            )}

            {isPastDue && (
              <div className="mt-3 p-3 bg-red-50 rounded-[var(--v-radius-md)]">
                <div className="flex items-center gap-2 text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4" />
                  Tu pago está atrasado. Actualiza tu método de pago para evitar perder acceso.
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-2"
                  onClick={handleUpdatePayment}
                  disabled={actionLoading}
                >
                  Actualizar método de pago
                </Button>
              </div>
            )}


            {isFree && !isTrialing && (
              <p className="mt-2 text-sm text-[var(--v-text-secondary)]">
                Estás en el plan gratuito.{' '}
                <a href="/pricing" className="text-[var(--v-teal-700)] hover:underline font-medium">
                  Mejora a Home+ por $3.99/mes
                </a>
              </p>
            )}
          </div>

          {!isFree && (
            <div className="flex flex-col gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleUpdatePayment}
                disabled={actionLoading}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Actualizar método de pago
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCancelModal(true)}
              >
                Cancelar suscripción
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* === PLANES DE UNIDAD / USUARIO (Entitlement Sprint 2, E1.1) === */}
      {unitPlans.length > 0 && (
        <Card className="mb-6 p-6">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 text-[var(--v-teal-700)]" />
            <h3 className="font-bold">Planes para tu hogar</h3>
          </div>
          <p className="text-sm text-[var(--v-text-secondary)] mb-4">
            Home+ se paga por unidad; HomeUltra cubre todas tus unidades con una sola suscripción.
          </p>
          {planCheckoutError && (
            <p role="alert" className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {planCheckoutError}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {unitPlans.map((plan) => {
              const isUltra = plan.id === 'home_ultra';
              const needsUnit = plan.id === 'home_plus';
              const activeUnit = needsUnit ? ownedUnits.find((unit) => unit.id === activeOwnedUnitId) : null;
              return (
                <div key={plan.id} className="rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-2)] p-4 flex flex-col">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-bold text-[var(--v-text-primary)]">{plan.name || (isUltra ? 'HomeUltra' : 'Home+')}</h4>
                    <span className="text-lg font-bold text-[var(--v-teal-700)]">
                      {formatPlanPrice(plan.computed_usd ?? plan.price_usd ?? 0)}<span className="text-xs font-normal text-[var(--v-text-secondary)]">/mes</span>
                    </span>
                  </div>
                  <p className="text-sm text-[var(--v-text-secondary)] flex-1">
                    {plan.description || (isUltra ? 'Home+ en todas tus unidades.' : 'Todos los módulos del hogar para una unidad.')}
                  </p>
                  <p className="text-xs text-[var(--v-text-secondary)] mt-2">
                    {isUltra ? 'Cobertura: todas tus unidades (presentes y futuras).' : `Cobertura: ${activeUnit ? `unidad activa (${activeUnit.household_name || activeUnit.unit_number})` : 'la unidad que tengas activa'}.`}
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-3"
                    disabled={planCheckoutLoading !== null || (needsUnit && !activeOwnedUnitId)}
                    onClick={() => handleUnitPlanCheckout(plan)}
                  >
                    {planCheckoutLoading === plan.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowUpRight className="w-4 h-4 mr-2" />}
                    Suscribir {isUltra ? 'HomeUltra' : 'Home+'}
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* === ESTADO DE CUENTA (Condo Charges) === */}
      <Card className="mb-6 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-5 h-5 text-[var(--v-teal-700)]" />
          <h3 className="font-bold">Estado de Cuenta</h3>
        </div>

        {chargesLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--v-teal-700)]" />
          </div>
        ) : chargesError ? (
          <div className="text-center py-6">
            <AlertTriangle className="w-8 h-8 text-[var(--v-danger)] mx-auto mb-2" />
            <p className="text-[var(--v-text-secondary)] text-sm">{chargesError}</p>
          </div>
        ) : (
          <>
            {/* Summary cards - always show */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="p-3 bg-[var(--v-surface-2)] rounded-lg">
                <p className="text-xs text-[var(--v-text-secondary)] mb-1">Saldo Actual</p>
                <p className={`text-lg font-bold ${totalBalance > 0 ? 'text-[var(--v-danger)]' : 'text-[var(--v-success)]'}`}>
                  {fmtMoney(totalBalance)}
                </p>
              </div>
              <div className="p-3 bg-[var(--v-surface-2)] rounded-lg">
                <p className="text-xs text-[var(--v-text-secondary)] mb-1">Total Pagado</p>
                <p className="text-lg font-bold text-[var(--v-success)]">{fmtMoney(totalPaid)}</p>
              </div>
              <div className="p-3 bg-[var(--v-surface-2)] rounded-lg">
                <p className="text-xs text-[var(--v-text-secondary)] mb-1">Próximos Venc.</p>
                <p className="text-lg font-bold text-[var(--v-text-primary)]">{upcomingCharges.length}</p>
              </div>
              <div className="p-3 bg-[var(--v-surface-2)] rounded-lg">
                <p className="text-xs text-[var(--v-text-secondary)] mb-1">Morosidad</p>
                <p className={`text-lg font-bold ${isDelinquent ? 'text-[var(--v-danger)]' : 'text-[var(--v-success)]'}`}>
                  {isDelinquent ? 'En mora' : 'Al corriente'}
                </p>
              </div>
            </div>

            {/* Próximos vencimientos */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[var(--v-warning)]" />
                Próximos Vencimientos
              </h4>
              {upcomingCharges.length === 0 ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-[var(--v-success-bg)] rounded-full flex items-center justify-center mx-auto mb-2">
                    <CheckCircle className="w-6 h-6 text-[var(--v-success)]" />
                  </div>
                  <p className="text-[var(--v-text-secondary)] text-sm">No hay cargos pendientes. Estás al corriente.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {upcomingCharges.map(charge => (
                    <div key={charge.id} className="flex items-center justify-between p-3 bg-[var(--v-surface-2)] rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-[var(--v-text-primary)]">{charge.description || charge.charge_type}</p>
                        <div className="flex items-center gap-2 text-xs text-[var(--v-text-secondary)] mt-1">
                          <Calendar className="w-3 h-3" />
                          Vence: {charge.due_date ? new Date(charge.due_date).toLocaleDateString('es-MX') : '—'}
                          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                            charge.status === 'overdue'
                              ? 'bg-[var(--v-danger-bg)] text-[var(--v-danger)]'
                              : 'bg-[var(--v-warning-bg)] text-[var(--v-warning)]'
                          }`}>
                            {charge.status === 'overdue' ? 'Vencido' : charge.status === 'partial' ? 'Pago parcial' : 'Pendiente'}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[var(--v-text-primary)]">{fmtMoney(charge.amount, charge.currency)}</p>
                        {charge.status === 'partial' && charge.paid_amount != null && (
                          <p className="text-xs text-[var(--v-text-secondary)]">Pagado: {fmtMoney(charge.paid_amount, charge.currency)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Historial de pagos */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-[var(--v-teal-700)]" />
                Historial de Pagos
              </h4>
              {condoPayments.length === 0 ? (
                <p className="text-[var(--v-text-secondary)] text-sm py-2">No hay pagos registrados.</p>
              ) : (
                <div className="space-y-2">
                  {condoPayments.map(payment => (
                    <div key={payment.id} className="flex items-center justify-between p-3 bg-[var(--v-surface-2)] rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-[var(--v-text-primary)]">{fmtMoney(payment.amount, payment.currency)}</p>
                        <div className="flex items-center gap-2 text-xs text-[var(--v-text-secondary)] mt-1">
                          <span>{new Date(payment.created_at).toLocaleDateString('es-MX')}</span>
                          <span className="capitalize">· {payment.payment_method}</span>
                          {payment.reference_number && <span>· Ref: {payment.reference_number}</span>}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.status === 'verified' || payment.status === 'paid'
                          ? 'bg-[var(--v-success-bg)] text-[var(--v-success)]'
                          : 'bg-[var(--v-warning-bg)] text-[var(--v-warning)]'
                      }`}>
                        {payment.status === 'verified' || payment.status === 'paid' ? 'Verificado' :
                         payment.status === 'pending_verification' ? 'En revisión' : payment.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* CTA: Pagar */}
            {upcomingCharges.length > 0 && (
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => window.location.href = '/reserva-pago'}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pagar ahora
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Subscription Payment History */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="w-5 h-5 text-[var(--v-teal-700)]" />
          <h3 className="font-bold">Historial de pagos de suscripción</h3>
        </div>

        {invoices.length === 0 ? (
          <div className="text-center py-8 text-[var(--v-text-secondary)] text-sm">
            No hay pagos de suscripción registrados todavía.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--v-border-light)] text-left">
                  <th className="py-2 pr-4 font-medium text-[var(--v-text-secondary)]">Fecha</th>
                  <th className="py-2 pr-4 font-medium text-[var(--v-text-secondary)]">Monto</th>
                  <th className="py-2 pr-4 font-medium text-[var(--v-text-secondary)]">Estado</th>
                  <th className="py-2 pr-4 font-medium text-[var(--v-text-secondary)]">Referencia</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-[var(--v-border-light)] last:border-0">
                    <td className="py-3 pr-4">
                      {inv.paid_at
                        ? new Date(inv.paid_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
                        : new Date(inv.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="py-3 pr-4 font-medium">
                      ${inv.amount_charged || (inv.amountUsd != null ? (inv.amountUsd / 100).toFixed(2) : undefined)} {inv.charged_currency || 'USD'}
                    </td>
                    <td className="py-3 pr-4">
                      <InvoiceStatusBadge status={inv.status} />
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs text-[var(--v-text-secondary)]">
                      {inv.payment_reference || inv.provider_ref || inv.id.slice(0, 8)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Trust footer */}
      <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[var(--v-text-secondary)]">
        <ShieldCheck className="w-4 h-4 text-[var(--v-success)]" />
        Pagos seguros procesados por Openpay
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <CancelModal
          planName={planDisplayName}
          loading={cancelLoading}
          onCancel={handleCancel}
          onClose={() => setShowCancelModal(false)}
        />
      )}
    </div>
  );
}

function StatusBadge({ status, isTrialing, isFree }: { status: string; isTrialing: boolean; isFree: boolean }) {
  if (isFree && !isTrialing) {
    return <span className="text-xs font-medium px-2 py-1 rounded-full bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]">Gratis</span>;
  }
  if (isTrialing) {
    return <span className="text-xs font-medium px-2 py-1 rounded-full bg-[var(--v-teal-50)] text-[var(--v-teal-700)]">Prueba gratuita</span>;
  }
  if (status === 'active') {
    return <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Activo</span>;
  }
  if (status === 'past_due') {
    return <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Pago atrasado</span>;
  }
  return <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-700">Inactivo</span>;
}

function InvoiceStatusBadge({ status }: { status: string }) {
  const map: Record<string, { color: string; label: string }> = {
    paid: { color: 'bg-green-100 text-green-700', label: 'Pagada' },
    awaiting_payment: { color: 'bg-amber-100 text-amber-700', label: 'Pendiente' },
    void: { color: 'bg-gray-100 text-gray-600', label: 'Cancelada' },
    past_due: { color: 'bg-red-100 text-red-700', label: 'Atrasada' },
    refunded: { color: 'bg-blue-100 text-blue-700', label: 'Reembolsada' },
  };
  const config = map[status] || { color: 'bg-gray-100 text-gray-600', label: status };
  return <span className={`text-xs font-medium px-2 py-1 rounded-full ${config.color}`}>{config.label}</span>;
}

function CancelModal({ planName, loading, onCancel, onClose }: {
  planName: string;
  loading: boolean;
  onCancel: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <Card className="max-w-md w-full p-6" >
        <div onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-bold">¿Cancelar suscripción?</h3>
            <button onClick={onClose} className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-[var(--v-text-secondary)] mb-4">
            ¿Seguro que deseas cancelar tu plan {planName}? Perderás acceso a:
          </p>
          <ul className="text-sm space-y-2 mb-6 text-[var(--v-text-secondary)]">
            <li className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Tareas ilimitadas</li>
            <li className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Mascotas ilimitadas</li>
            <li className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Todos los módulos premium</li>
            <li className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Soporte prioritario</li>
          </ul>
          <div className="p-3 bg-[var(--v-surface-2)] rounded-[var(--v-radius-md)] mb-4">
            <p className="text-xs text-[var(--v-text-secondary)]">
              <Pause className="w-4 h-4 inline mr-1" />
              ¿Sabías que puedes pausar tu suscripción 1 mes sin perder tus datos?
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" fullWidth onClick={onClose}>
              Mantener plan
            </Button>
            <Button variant="primary" fullWidth onClick={onCancel} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Cancelar plan
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
